// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../validation/runtime-checks/index.js';
import { Text } from '../Text.js';
import Latex from '../Latex.js';
import { List } from '../List.js';
import Image from '../Image.js';
import DraftImage from '../DraftImage.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import { CartesianPlane } from '../CartesianPlane.js';
import { NumberLine } from '../NumberLine.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { TwoColumnProof } from '../TwoColumnProof.js';
import { AlgebraicWorking } from '../AlgebraicWorking.js';
import { EngageNoteCollapsibleInternal } from './EngageNoteCollapsibleInternal.js';
import { illustrationColor, productColor } from '../designSystem.js';
import {
  DisabilityIcon,
  EnglishLanguageLearnerIcon,
} from '../InstructionalSupportIcons.js';
import { Heading } from '../Heading.js';
import { LatexTextAtom } from '../LatexTextAtom.js';

export function EANPreparationSection(props: {|
  children: [
    | Element<typeof EANPreparationList>
    | [
        Element<typeof EANPreparationList>,
        Element<typeof EANDisabilitySupportPanel>,
      ]
    | [
        Element<typeof EANPreparationList>,
        Element<typeof EANEnglishSupportPanel>,
      ]
    | [
        Element<typeof EANPreparationList>,
        Element<typeof EANDisabilitySupportPanel>,
        Element<typeof EANEnglishSupportPanel>,
      ],
  ],
|}) {
  const { failedValidation } = usePropsValidation(props, EANPreparationSection);
  if (failedValidation) return null;
  return (
    <div style={{ color: productColor.shuttleGrey }}>
      <Heading>Preparation and materials</Heading>
      <div>{props.children}</div>
    </div>
  );
}
// both panels are optional, but disability comes first if both exist
EANPreparationSection.spec = {
  children: t.exactOrderChildrenArray(
    t.elementType(EANPreparationList),
    t.optional(t.elementType(EANDisabilitySupportPanel)),
    t.optional(t.elementType(EANEnglishSupportPanel)),
  ),
};

export function EANPreparationList(props: {|
  children: ChildrenArray<Element<typeof EANPreparationListItem>>,
|}) {
  const { failedValidation } = usePropsValidation(props, EANPreparationList);
  if (failedValidation) return null;
  return (
    <div>
      <ul style={{ listStyle: 'disc' }}>{props.children}</ul>
    </div>
  );
}
EANPreparationList.spec = {
  children: t.childrenArray(t.elementType(EANPreparationListItem)),
};

export function EANPreparationListItem(props: {|
  children: ChildrenArray<
    string | Element<typeof Latex> | Element<typeof LatexTextAtom>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    EANPreparationListItem,
  );
  if (failedValidation) return null;
  return <li>{props.children}</li>;
}
EANPreparationListItem.spec = {
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
    ),
  ),
};

export function EANDisabilitySupportPanel(props: {|
  subheading: string,
  children: ChildrenArray<
    | Element<typeof Text>
    | Element<typeof Latex>
    | Element<typeof LatexTextAtom>
    | Element<typeof List>
    | Element<typeof Image>
    | Element<typeof DraftImage>
    // probably only need those above this line
    | Element<typeof BoxAndWhiskerPlot>
    | Element<typeof CartesianPlane>
    | Element<typeof NumberLine>
    | Element<typeof StemAndLeafPlot>
    | Element<typeof TabularData>
    | Element<typeof TwoColumnProof>
    | Element<typeof AlgebraicWorking>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    EANDisabilitySupportPanel,
  );
  if (failedValidation) return null;
  return (
    <EngageNoteCollapsibleInternal
      title="Support students with disabilities"
      icon={<DisabilityIcon />}
    >
      <SupportPanelSubheadingInternal>
        {props.subheading}
      </SupportPanelSubheadingInternal>
      {props.children}
    </EngageNoteCollapsibleInternal>
  );
}
EANDisabilitySupportPanel.spec = {
  subheading: t.string,
  children: t.childrenArray(
    t.unionOfTypes(
      // Primitives
      t.elementType(Text),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(List),
      t.elementType(Image),
      t.elementType(DraftImage),
      // probably only need those above this line
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(TabularData),
      t.elementType(TwoColumnProof),
      t.elementType(AlgebraicWorking),
    ),
  ),
};

export function EANEnglishSupportPanel(props: {|
  subheading: string,
  children: ChildrenArray<
    | Element<typeof Text>
    | Element<typeof Latex>
    | Element<typeof LatexTextAtom>
    | Element<typeof List>
    | Element<typeof Image>
    | Element<typeof DraftImage>
    // probably only need those above this line
    | Element<typeof BoxAndWhiskerPlot>
    | Element<typeof CartesianPlane>
    | Element<typeof NumberLine>
    | Element<typeof StemAndLeafPlot>
    | Element<typeof TabularData>
    | Element<typeof TwoColumnProof>
    | Element<typeof AlgebraicWorking>,
  >,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    EANEnglishSupportPanel,
  );
  if (failedValidation) return null;
  return (
    <EngageNoteCollapsibleInternal
      title="Support for English language learners"
      icon={<EnglishLanguageLearnerIcon />}
    >
      <SupportPanelSubheadingInternal>
        {props.subheading}
      </SupportPanelSubheadingInternal>
      {props.children}
    </EngageNoteCollapsibleInternal>
  );
}
EANEnglishSupportPanel.spec = {
  subheading: t.string,
  children: t.childrenArray(
    t.unionOfTypes(
      // Primitives
      t.elementType(Text),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(List),
      t.elementType(Image),
      t.elementType(DraftImage),
      // probably only need those above this line
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(NumberLine),
      t.elementType(StemAndLeafPlot),
      t.elementType(TabularData),
      t.elementType(TwoColumnProof),
      t.elementType(AlgebraicWorking),
    ),
  ),
};

function SupportPanelSubheadingInternal({ children }) {
  return (
    <p
      style={{
        fontWeight: 600,
        color: illustrationColor.regentGray,
        fontSize: 16,
        lineHeight: '22px',
        marginBottom: 16,
      }}
    >
      {children}
    </p>
  );
}
